/* ══ v17.3 (W1, F-P1-02): THE SYNCHRONOUS HALF OF SAVE NEEDED A CLEAR SITE TOO ═══════════
   THE DEFECT. `_saveInFlight` was set, then six synchronous steps ran with no try/catch, and
   the only two clear sites both hang off the write promise. A throw in those steps left the
   flag stuck `true` for the life of the tab: every later Save hit `if (_saveInFlight) return;`
   and did nothing — no dialog, no toast, not even a console line. The user kept working and
   lost everything since the last real save on tab close. Nothing on disk was ever at risk;
   the damage was that the app stopped accepting work while still looking normal.

   ⚠ WHY A WRAPPER RATHER THAN A try/catch AROUND THE BLOCK. Wrapping in place would mean
   either re-indenting ~235 lines — burying this fix in a whitespace diff — or hoisting seven
   `const`s out to `let` so the completion could still see them, which trades an immutability
   guarantee for nothing. The body is unchanged and unmoved; this is the clear site.

   ⚠ IT ALSO COVERS ONE CASE THE BLOCK ITSELF WOULD NOT: if `_srvPostSave` ever returned a
   non-promise, `.then` would throw synchronously AFTER the block, and the chain that clears the
   flag would not exist. That throw lands here.

   ⚠ NOTHING IS SWALLOWED. The thrown error's OWN message is shown, because serializeDB's
   v16.1 (d) refusal is a designed message that names the project at fault — replacing it with a
   generic "save failed" would undo that whole item. Console first, so a developer sees the
   stack even if the user dismisses the dialog.

   ⚠ THE FLAG IS CLEARED, NOT LEFT FOR THE NEXT SAVE TO SORT OUT. Save-is-never-refused is
   standing law for a failure BEFORE persistence. v17.4 W3 adds the one honest exception: after
   bytes land but memory cannot reconcile, a fresh reload is safer than another write. */
function _finishSaveRepaint() {
  /* v17.4 (W3): both are presentation after the persistence/commit decision. A missing badge or
     repaint must be visible in the console, but it cannot turn a completed atomic write into a
     “nothing was written” result. Keep the two catches separate so one broken painter cannot
     prevent the other from getting its chance. */
  try { _updateDataSourceLine(); }
  catch (e) { console.warn('The data-source line could not be refreshed after Save.', e); }
  try { _repaintFileState(); }
  catch (e) { console.warn('The file-state view could not be repainted after Save.', e); }
  try { _repaintLastSavedDates(); }
  catch (e) { console.warn('The Last saved dates could not be repainted after Save.', e); }
}
function exportFile() {
  try {
    return _exportFileBody();
  } catch (err) {
    /* The write never started, so the previous file is untouched and the work is still in
       memory — say both, and roll back the in-memory stamp the same way the write-failure
       branch does, or the header shows an "As of" for a save that did not happen. */
    if (_pendingSaveStamp) {
      _unstampSaveTime(_pendingSaveStamp);
      _pendingSaveStamp = null;
      renderHeaderMeta();
    }
    _saveDateView = null;
    try { _repaintLastSavedDates(); }
    catch (paintErr) { console.warn('The Last saved dates could not be repainted after a failed Save.', paintErr); }
    _saveInFlight = false;
    isDirty = true;
    _setSaveDirty();
    _allSaveBtns().forEach(b => { b.innerHTML = _SAVE_IDLE_HTML; });
    console.error('Save preparation failed', err);
    alert('Save FAILED \u2014 your changes are NOT saved.\n\n'
        + ((err && err.message) ? err.message + '\n\n' : '')
        + 'Nothing was sent to the server, so it still holds the last good version, and '
        + 'your work is still open in this tab. Fix the cause and press Save again.');
  }
}
function _exportFileBody() {
  if (_progressBlockSaveOrExport()) return;
  /* v5.0 integrity guard: refuse to save if the portfolio did not load cleanly —
     never publish an empty or partial picture over the server's real data. */
  if (!_dbLoadOk) {
    alert('Save blocked.\n\nThe portfolio did not load cleanly from the server, so saving now could overwrite good data with an empty or partial picture. Reload the page and try again.');
    return;
  }
  /* ── v17.4 (W3, F-B-04): the preceding Save reached atomic close, but its in-memory commit
     threw. The bytes are good and the same-part timestamp below was advanced from those exact
     bytes; what is not trustworthy is this tab's mixture of overlays, revisions and bindings.
     Do not send that mixture through another Save. A new page reconstructs it from the file. */
  if (_saveNeedsReload) {
    alert('Save is blocked in this tab.\n\nThe previous Save reached the data file, but this tab could not finish updating itself. '
        + 'Note or export any newer changes you made while that Save was running, then reload this tab before continuing.');
    return;
  }
  /* ── v13.1 (A64): RE-ENTRANCY. There was no in-flight flag and _setSaveSaving only swaps
     classes, so the button stayed enabled and a double-click started a SECOND overlapping
     write with its own _stampSaveTime(). Depending on which landed last the user got either
     a two-tab freshness refusal on the NEXT real save, or a full "Save FAILED - your changes
     are NOT saved" alert for a save that had in fact succeeded. Registered as A64. */
  if (_saveInFlight) return;
  /* P5 SD-01: freeze the date already visible before the optimistic timestamp is staged.
     Only the confirmed bytes may advance this presentation snapshot. */
  _saveDateView = { before: window.__DB__.savedAt, confirmed: null };
  _saveInFlight = true;
  /* v10.4.2 (W3): show the SAVING state before any of the work below. Set first so it is
     in the DOM for the paint that follows this synchronous block — the capture and the
     serialise both run before the write, and on a large file that is exactly the stretch
     during which the button used to show nothing at all. */
  _setSaveSaving();
  /* Step 1 - CAPTURE (both paths) */
  _captureAllFromDOM();
  /* ── v13.1 (A01): WHO are we saving? The bytes are built from the CURRENT project, but
     the completion runs after an await and _commitSave writes through the module-level
     ACTIONS / newActions / deletedIds bindings — which setActiveProject RE-POINTS. Nothing
     suspends navigation during a save, so clicking another project mid-write made
     _commitSave splice the SAVING project's baked actions into the INCOMING project's
     array (that array IS __DB__.projects[incoming].actions), drop its pending adds and
     commit its pending deletes. Reproduced in the v13 review: B's actions became A's.
     The undo slot got a projectId stamp in v10.3 for precisely this reason; the save
     pipeline was the one asynchronous continuation that never did. Stamp it. */
  const _saveProjectId = activeProjectId;
  /* ── v13.1 (A05): the success branch used to clear isDirty UNCONDITIONALLY, so an edit
     made while the write was in flight — and therefore NOT in the bytes just written — was
     silently marked saved: dot cleared, button green, beforeunload guard disarmed. Snapshot
     the edit counter now, immediately after the capture that froze the bytes. */
  const _dirtyAtCapture = _editSeq;
  /* Step 2 - STAMP (save only, and BEFORE serialising - see the pipeline note below) */
  const _stamped = _stampSaveTime();
  /* v17.3 (W1): from here until the save settles, a throw owes the header a rollback. */
  _pendingSaveStamp = _stamped;
  /* Step 3 - BUILD the exact bytes, without mutating anything */
  const _baked = _buildActionObjects();
  /* ⚠ v16.8 (W1): WHAT WAS PENDING AT THE MOMENT THE BYTES WERE FROZEN. Snapshotted on the
     same synchronous line as the bake, so anything the user does during the write falls
     outside it by construction and _commitSave keeps it. */
  const _captured = { deletes: new Set(deletedIds),
                      newIds: new Set(newActions.map(a => a && a.id)) };
  /* v14 (W4a): which records changed since the last publish. PURE — it returns a plan and
     touches nothing, so it belongs in step 3; step 4 applies it, on success only.

     ⚠⚠ v15.3 (a): PLANNED AGAINST THE BYTES, NOT AGAINST MEMORY — and this was losing every
     piece of an action's merge bookkeeping since v14.
     This read `window.__DB__`, where an action edit IS NOT YET PRESENT: edits live in the
     module-level `edits` overlay, which `initActionEdits` builds as a SEPARATE object by
     copying values out of the record, and `ACTIONS` is only replaced by the baked list in
     step 4's `_commitSave` — after this line, and after the write has resolved. So the plan
     compared the PRE-EDIT action against the baseline, found them equal and stamped nothing,
     while `_saveDB` below carried the edit. Three consequences, one blind spot:
       · an edited action was written with NO rev, so the merge had no counter to prefer it
         and a colleague's stale copy settles the record on the hash tie-break instead —
         `normalizeAppMeta`'s v15 hazard ("a colleague still holding my rev 5 now beats my
         rev 1"), reached by a different route;
       · a DELETED action produced NO tombstone, because _revPlan's "in the baseline, gone
         now" test ran against a live ACTIONS that still held it — only the baked list omits
         it — so delete-beats-stale-copy never ran for actions at all;
       · and `_mintLogEntry` walks plan.stamps, so there was never a ledger row: "changing
         priority or due date does nothing" was this, exactly.
     Measured in his own part before the fix: four actions with no `rev` and no `writer`,
     beside stakeholders carrying 2..5 and sessions carrying 1, 5 and 10.
     ⚠ `_buildSaveDB` with a NULL plan is pure and returns BEFORE it stages, so this is the
     same shape the bytes are built from and step 3's "without mutating anything" contract is
     untouched. `_revApply(window.__DB__, _plan)` in step 4 still lands, because `_commitSave`
     has put the baked objects into ACTIONS by then. And it does NOT mass-restamp on the first
     save after upgrade: the baseline has always been captured from `_saveDB`, so it already
     holds the baked content and an untouched action still compares equal. */
  const _planned = _buildSaveDB(null, _baked, null);
  const _plan = _revPlan(_planned, _revBaseline, _myWriterId());
  const _saveDB = _buildSaveDB(null, _baked, _plan);
  /* v18: the plan IS the save. Each stamp is one changed record and each tomb one deletion,
     sent with the rev this tab last saw; the server applies all of them or none. */
  const _changes = _srvPlanToChanges(_saveDB, _plan);
  let _logEntry = null;
  try { _logEntry = _mintLogEntry(_saveDB, _revBaseline, _plan, null); }
  catch (e) { console.warn('The activity log could not be minted for this save.', e); }

  /* v10.3 (W2): THE WRITE IS THE ONLY THING THAT MAKES A SAVE REAL. Dirty stays set, the date is
     rolled back on failure, and only the resolved success branch clears anything.
     v17.4 (W3, F-B-04): `_writePersisted` is the truth boundary for the shared catch — true once
     the server has committed, after which a completion failure must not claim nothing was saved. */
  let _writePersisted = false;
  _srvPostSave(_changes, _logEntry).then(res => {
    if (res && res.ok) {
      _writePersisted = true;
      const _persistedAt = (res.savedAt || _saveDB.savedAt);
      window.__DB__.savedAt = _persistedAt;
      _dataFileSavedAt = _persistedAt;
      if (_saveDateView) _saveDateView.confirmed = _persistedAt;
      try { _repaintLastSavedDates(); }
      catch (e) { console.warn('The Last saved dates could not be repainted after write confirmation.', e); }
      /* ── v13.1 (A01): if the user navigated mid-save, the server holds the right data either
         way; what must not happen is applying the saving project's bake through bindings that
         now belong to a DIFFERENT project. */
      const _switchedMidSave = (activeProjectId !== _saveProjectId);
      /* Step 4 - COMMIT (save only; nothing here may change what was sent) */
      if (_switchedMidSave) {
        const _who = (window.__DB__.projects.find(c => c && c.id === _saveProjectId) || {}).name
                     || _saveProjectId;
        showToast('Saved — you switched project while it was saving, so reopen “' + _who +
                  '” to see its saved state.', { type: 'warn', duration: 8000 });
      } else {
        _commitSave(_baked, _captured);
      }
      /* ── v14 (W4a): THE SAVE SUCCEEDED, SO THE REVS ARE NOW TRUE. Stamped on the live records
         here and nowhere else, so a refused save costs nothing. The new baseline comes from what
         was SENT (`_saveDB`), not from memory, so an edit made during the request is still seen
         as unpublished by the next save. */
      _revApply(window.__DB__, _plan);
      _revTombs = _revTombs.concat(_revTombRecords(_plan));
      _revBaseline = _revCaptureBaseline(_saveDB);
      _srvPushLocalAppMeta(_saveDB);
      _srvPushShared(_saveDB);
      _srvColleagues = [];
      _srvAfterOwnSave(res);
      /* ── v13.1 (A05): only clear the dirty flag if nothing was edited while the save was in
         flight; _commitSave (v16.8 W1) keeps that newer work in memory for the next save. */
      const _editedMidSave = (_editSeq !== _dirtyAtCapture);
      isDirty = _editedMidSave;
      if (_editedMidSave) {
        _setSaveDirty();
        showToast('Saved — but you have newer changes that are not saved yet. Save again.',
                  { type: 'warn', duration: 7000 });
      } else {
        _setSaveSaved();
      }
      setTimeout(() => { if (!isDirty) _setSaveIdle(); }, 2500);   /* never wipe a dirty dot */
    } else {
      _unstampSaveTime(_stamped);                  /* don't show an "As of" that never happened */
      renderHeaderMeta();
      isDirty = true;
      _setSaveDirty();
      _allSaveBtns().forEach(b => { b.innerHTML = _SAVE_IDLE_HTML; });
      if (res && res.conflicts) {
        /* v18: a colleague saved one of these records first. NOTHING was written. */
        _srvShowConflict({ conflicts: res.conflicts, changes: _changes, plan: _plan,
                           saveDB: _saveDB, baseline: _revBaseline });
      } else if (res && res.auth) {
        /* The login dialog is already up (the 401 raised it); the edits stay in this tab. */
        showToast(res.reason, { type: 'warn', duration: 9000 });
      } else {
        alert('Save FAILED \u2014 your changes are NOT saved.\n\n' +
              ((res && res.reason) ? res.reason + '\n\n' : '') +
              'Nothing was written, so the server still holds the last good version, and your work is '
              + 'still open in this tab. Press Save again' + ((res && res.network) ? ' once the server is back.' : '.'));
      }
    }
    _finishSaveRepaint();
  }).catch(err => {
    /* v17.4 (W3, F-B-04): before persistence, roll back the optimistic stamp and leave a usable
       retry. After persistence, keep the landed state, block this uncertain tab and tell the user
       to reload. */
    isDirty = true;                       /* retains newer mid-save work behind beforeunload */
    _setSaveDirty();
    _allSaveBtns().forEach(b => { b.innerHTML = _SAVE_IDLE_HTML; });
    if (_writePersisted) {
      _saveNeedsReload = {
        savedAt: _saveDB.savedAt,
        reason: (err && err.message) ? err.message : String(err || 'Save completion failure')
      };
      console.error('Save reached the server but tab completion failed', err);
      alert('Your changes were saved, but this tab could not finish updating itself.\n\n'
          + ((err && err.message) ? err.message + '\n\n' : '')
          + 'Saving is now blocked in this tab because its in-memory state may not match the server. '
          + 'Note or export any newer changes made while Save was running, then reload this tab.');
    } else {
      _unstampSaveTime(_stamped);
      renderHeaderMeta();
      console.error('Save failed before persistence', err);
      alert('Save FAILED — your changes are NOT saved.\n\n'
          + ((err && err.message) ? err.message + '\n\n' : '')
          + 'Nothing was written, so the server still holds the last good version, and '
          + 'your work is still open in this tab. Fix the cause and press Save again.');
    }
    _finishSaveRepaint();
  }).finally(() => {
    _saveInFlight = false;
    _pendingSaveStamp = null;
    _saveDateView = null;
    try { _repaintLastSavedDates(); }
    catch (e) { console.warn('The Last saved dates could not be repainted after Save settled.', e); }
  });
}

/* Step 2 - STAMP. Save only, and it must run BEFORE serialising: both values are written
   into the file. appMeta.lastSaved remains legacy compatibility metadata; the three visible
   dates read the confirmed file savedAt snapshot. Returns the previous pair for rollback. */
function _stampSaveTime() {
  const prev = { savedAt: window.__DB__.savedAt, lastSaved: appMeta.lastSaved };
  const d = new Date();
  appMeta.lastSaved = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  window.__DB__.savedAt = d.toISOString();
  renderHeaderMeta();
  return prev;
}
function _unstampSaveTime(prev) {
  if (!prev) return;
  window.__DB__.savedAt = prev.savedAt;
  appMeta.lastSaved = prev.lastSaved;
}

/* Step 3a - the baked action list for the ACTIVE project: pending adds folded in,
   deletes dropped, orphan edits pruned. COMPUTED, never applied - applying it is
   step 4's job, so that an export can produce identical bytes without committing. */
function _buildActionObjects() {
  return _bakeActionsFor(ACTIONS, newActions, deletedIds, edits, false);
}
/* ── ⚠ v16.8 (W1): the bake, for ANY project's containers ──────────────────────────
   Extracted unchanged from _buildActionObjects so the ACTIVE project's bytes are produced by
   the same code as before — this is a parameterisation, not a rewrite.
   ⚠⚠ `keepUnknown` IS THE ONE BEHAVIOURAL DIFFERENCE, AND IT IS DELIBERATE. The mapper below
   rebuilds every action FIELD BY FIELD, so a key it does not name is dropped (v12.2 W3 found
   this, v14 W2 restored rev/writer/del through it). That is settled behaviour for the project
   the user has open. It must NOT be silently extended to every OTHER project in the portfolio:
   those actions may carry keys written by a version this shell does not model, and dropping
   them on a save the user did not even make in that project would be exactly the "silently
   lossy" outcome the schema rule forbids. So the non-active bake layers the mapped fields ON
   TOP of the record and keeps anything it does not understand. ⚠ The asymmetry is the safe
   direction, and it disappears the moment that project is opened. */
function _bakeActionsFor(acts, news, dels, ed, keepUnknown) {
  const allForExport = [...acts, ...news].filter(a => !dels.has(a.id));
  /* v2.8: prune any orphan edit entries (keys without a matching action being saved).
     Catches orphans introduced by any code path, including pre-existing ones from
     older versions. */
  {
    const keepIds = new Set(allForExport.map(a => a.id));
    Object.keys(ed).forEach(k => { if (!keepIds.has(Number(k))) delete ed[k]; });
  }
  // Build a clean JSON-shaped object for each action, then serialize the whole array with JSON.stringify
  return allForExport.map(a => {

    /* v10.3 (W1, second defence): never dereference a missing edits record. The v10.2
       orphan-prune bug could delete one, and this mapper then threw HERE — before
       anything was written — so Save became a silent no-op. Re-create it instead of
       throwing: a save must never be lost to a bookkeeping slip. */
    let e = ed[a.id];
    if (!e) { initActionEdits(a, ed); e = ed[a.id]; }
    return Object.assign(keepUnknown ? Object.assign({}, a) : {}, {
      /* v14 (W2): this mapper rebuilds every action field by field, so a key absent HERE is
         dropped on Save even though `actions` is serialised wholesale — the v12.2 find.
         Conditional spreads keep rev/writer/del LAZY: absent stays absent, byte-identically. */
      ...(a.rev    === undefined ? {} : { rev: a.rev }),
      ...(a.writer === undefined ? {} : { writer: a.writer }),
      ...(a.del    === undefined ? {} : { del: a.del }),
      id: a.id,
      date: actField(e, a, 'date'),
      source: actField(e, a, 'source'),
      desc: actField(e, a, 'desc'),
      owner: actField(e, a, 'owner'),
      due: e.due || null,
      status: e.status,
      priority: e.priority,
      details: e.details || null,
      labels: Array.isArray(e.labels) ? [...e.labels] : [],  /* v2.40: replaces category */
      requestor: e.requestor || null,
      closedDate: e.closedDate || null,  /* v2.20 */
      dependsOn: Array.isArray(e.dependsOn) ? [...e.dependsOn] : [],  /* v2.21 */
      relatedTo: Array.isArray(e.relatedTo) ? [...e.relatedTo] : [],  /* v2.21 */
      progress: (typeof e.progress === 'number') ? e.progress : null,  /* v2.22 */
      flagged: !!e.flagged,  /* v2.22 */
      parentId: (typeof e.parentId === 'number') ? e.parentId : null,  /* v2.90: sub-action support */
      // Deep-clone updates so saved-into-ACTIONS array is independent of edits' array (avoids reference aliasing)
      updates: (e.updates || []).map(u => ({...u})),
      /* ── v12.2 (W3): carry the lazy noWbs flag through the SAVE BAKE ───────────────
         ⚠ This mapper rebuilds every action FIELD BY FIELD, so a key absent from this list is
         dropped on Save - and then out of MEMORY too, because _commitSave splices the baked
         list into ACTIONS. "The serialiser is wholesale" is NOT enough for actions: the bake
         runs first. Found by reading this function before trusting that assumption.
         The conditional spread keeps it LAZY - an action without the flag emits nothing, so a
         project that never used it serialises byte-identically. */
      ...(a.noWbs ? { noWbs: true } : {})
    });
  });
}

/* Step 1 - CAPTURE, in full. Pulls everything the user has typed but not yet
   blurred into the model, plus the derived per-meeting diff. Runs on BOTH paths:
   a backup that ignored unblurred typing would not be a backup of what is on
   screen. Mutates the model only in ways a save and an export must share. */
function _captureAllFromDOM() {
  /* v4.0: fold the live meeting timer's running segment into elapsedMs so the
     saved file carries the up-to-date accrued time (never a wall-clock start). */
  if (typeof _mtFlush === 'function') _mtFlush();
  // === REAL FIX (after multiple wrong attempts): the bug is that document.documentElement.outerHTML
  //     serializes <textarea> using its innerHTML (defaultValue), NOT its current .value.
  //     When user types, .value updates but innerHTML stays whatever was set at last renderSessions.
  //     So outerHTML captures stale (empty) textarea content, even though SESSIONS data is correct.
  //     On reopen, renderSessions doesn't run automatically (only on tab-switch to notes),
  //     so the user sees the stale textarea content and thinks save didn't work.
  //
  //     Workarounds that "happened to work":
  //       - Click sort → toggleSessionSort calls renderSessions → DOM textareas get rewritten
  //       - Switch to actions tab → save → reopen lands on actions → click notes → renderSessions fires
  //
  //     The fix: after syncing DOM → SESSIONS, also write each form element's current .value
  //     into the DOM's innerHTML/value attribute, so outerHTML serializes the live state.

  // Step 1: sync DOM → SESSIONS (so JSON.stringify produces correct data)
  syncSessionsFromDOM();

  // v2.11: capture current UI state into appMeta.uiState so reopen restores the same view.
  // v8.6.3: the snapshot itself moved to captureUIState() (pure extraction, plus the new
  // collapsedSections key) so the QA harness can exercise the capture without a real save.
  captureUIState();

  // v2.6: strip .has-update class from action cards before outerHTML capture.
  // Saved file will not contain the yellow-highlight class; ensures yellow doesn't
  // persist on reopen even if init render() were skipped for any reason.
  document.querySelectorAll('.action-card.has-update').forEach(el => el.classList.remove('has-update'));

  // Step 2: write current .value back into DOM attributes/text so outerHTML serializes correctly
  document.querySelectorAll('.session-card').forEach(card => {
    card.querySelectorAll('input').forEach(inp => {
      // For <input>: outerHTML uses the value attribute (defaultValue), not .value (current).
      inp.setAttribute('value', inp.value);
    });
    card.querySelectorAll('textarea').forEach(ta => {
      // For <textarea>: outerHTML uses innerHTML (defaultValue), not .value (current).
      // Setting textContent updates the text node child = defaultValue, without firing input events.
      ta.textContent = ta.value;
    });
  });
  /* v3.0: same treatment for Dashboard #dashNotes textarea. Also sync the in-memory
     appMeta.dashboard.notes in case the user typed without blurring before saving. */
  {
    const dashTa = document.getElementById('dashNotes');
    if (dashTa) {
      appMeta.dashboard = appMeta.dashboard || {};
      appMeta.dashboard.notes = dashTa.value || '';
      dashTa.textContent = dashTa.value;
    }
  }
  /* v17.6: Save and Export share the same ownership-checked capture. */
  _captureStakeholderCards();

  /* v2.40: update the CURRENT meeting's diff against the current baseline. The baseline
     itself (sessionSnapshot) is NOT modified by save - it only changes via
     addSession, duplicateLatestSession, or resetTracker.
     ⚠ v14 (W4b): this said SESSIONS[0], which after a merge is the OLDEST meeting, not the
     newest — so every save would have written the live action diff onto the wrong meeting and
     overwritten whatever that one recorded. See _mostRecentSession. */
  const currentSnapshot = takeSnapshot();
  const _curSession = _mostRecentSession();
  if (_curSession && sessionSnapshot) {
    _curSession.diff = computeDiff(sessionSnapshot, currentSnapshot);
  }
  // Persist the current sessionSnapshot into appMeta. This survives a save and
  // lets us restore the meeting-start baseline on a same-day reopen.
  appMeta.sessionSnapshot = sessionSnapshot;
}

/* Step 3b - the exact db object to serialise, WITHOUT mutating window.__DB__.
   ids === null -> the whole DB (a save, or a select-all export); otherwise only those
   project ids (the subset export). The active project is substituted by a shallow
   clone carrying the baked actions, so `newActions` writes [] exactly as it always
   has while the live arrays stay untouched until commit.
   v14 (W4a): `plan` is a `_revPlan` result. Given one, the returned db is a STAGED COPY
   carrying this save's rev/writer stamps and its tombstones — see the block at the end. */
/* ── ⚠⚠ v16.8 (W1): EVERY PROJECT IS BAKED, NOT ONLY THE ACTIVE ONE ─────────────────
   The map in _buildSaveDB used to read
       (baked && c.id === activeProjectId) ? clone-with-baked : c
   and that ONE expression carried TWO of SWEEP-2's twelve findings:

   · `: c` passed every OTHER project through BY REFERENCE, so a project you edited and then
     LEFT never met its own pending deletions or its own edits overlay. The deletion was never
     written at all and came back on the next reload; the edit reached disk only in the
     project's `edits` overlay while its action RECORD kept the old value — two representations
     of one action disagreeing on disk, which a colleague's merge can later resolve the wrong
     way round. Ordinary use: tidying one project, then moving to another before saving.
     (SWEEP-2 finding 1. ⚠ It is also reachable from the PORTFOLIO, where selectProgramme
     nulls activeProjectId so NOTHING matched and nothing was baked.)

   · `c.id === activeProjectId` matched on the NAME-DERIVED routing id. Two colleagues who each
     create "Rollout" both get `rollout`; the merge keeps both correctly because it keys on
     `uid`, but this wrote the active project's baked actions into BOTH of them — one person's
     work silently replaced by the other's. (SWEEP-2 finding 10 / the audit's R1.)

   ⚠⚠ BAKING EACH PROJECT FROM ITS OWN CONTAINERS FIXES BOTH AT ONCE, AND IT REMOVES THE
   COMPARISON RATHER THAN CORRECTING IT: there is no longer any question of which project
   "matches", so a duplicated routing id cannot mislead this code at all. P0 named the seam —
   projects are the only collection the merge keys on `uid` while everything else keys on `id`
   — and this function is where the two disagreed.

   ⚠ THE ACTIVE PROJECT STILL USES THE CALLER'S `baked` LIST, unchanged: same bytes, and the
   caller's single bake is not recomputed. Identity is settled by the CONTAINER, not by an id
   — setActiveProject points ACTIONS at that project's own array — which is the one test two
   projects sharing a routing id cannot confuse.
   ⚠ A project with nothing pending is PASSED THROUGH BY REFERENCE exactly as before, so a
   portfolio where only one project was touched serialises byte-identically. */
function _bakeProjectForSave(c, baked) {
  if (!c || typeof c !== 'object' || c.del) return c;   /* a tombstone has nothing to bake */
  const isActive = Array.isArray(c.actions) && c.actions === ACTIONS;
  if (baked && isActive) return Object.assign({}, c, { actions: baked, newActions: [] });
  const acts = Array.isArray(c.actions) ? c.actions : [];
  const news = Array.isArray(c.newActions) ? c.newActions : [];
  const dels = (c._deletedIds instanceof Set) ? c._deletedIds : null;
  const ed   = (c.edits && typeof c.edits === 'object' && !Array.isArray(c.edits)) ? c.edits : null;
  /* Nothing pending and no overlay to fold in: leave the record completely alone. */
  if (!news.length && !(dels && dels.size) && !ed) return c;
  /* ⚠ The overlay is pruned on a COPY. Step 3's contract is that it builds the bytes without
     mutating anything, and the live map belongs to a project the user is not even in. */
  const edCopy = ed ? Object.assign({}, ed) : {};
  const list = _bakeActionsFor(acts, news, dels || new Set(), edCopy, true);
  const keepIds = new Set(list.map(a => a && a.id));
  Object.keys(edCopy).forEach(k => { if (!keepIds.has(Number(k))) delete edCopy[k]; });
  return Object.assign({}, c, { actions: list, newActions: [], edits: edCopy });
}
function _buildSaveDB(ids, baked, plan) {
  const db = window.__DB__;
  const list = Array.isArray(db.projects) ? db.projects : [];
  const projects = list
    .filter(c => !ids || ids.indexOf(c.id) !== -1)
    .map(c => _bakeProjectForSave(c, baked));
  let programmes = Array.isArray(db.programmes) ? db.programmes : undefined;
  /* ── v13.1 (A11): a SUBSET export must not ship the whole portfolio ────────────────────
     This filtered `projects` and passed `programmes` through untouched, so exporting one
     project to a colleague or a partner also handed over the names, RAG levels, RAG
     rationales and complete benefits registers of every OTHER programme — and the
     rationales are candid free text. The dialog says "only the selected project(s)".
     On import, _applyImportedProgrammes then creates every one of them in their file.
     ⚠ Deliberately only when the selection is a genuine SUBSET. `ids === null` is the SAVE
     path and must stay byte-identical; and a select-ALL export is offered as a
     full-portfolio backup, so it must still carry a programme that currently has no
     projects at all — dropping that would make a "backup" quietly lossy and would break
     export_equals_save, which compares exactly that case against a save. */
  if (ids && Array.isArray(programmes) && projects.length < list.length) {
    const keptProjects = new Set(projects.map(c => c && c.id));
    const keptProgs = new Set(projects.map(c => c && c.programmeId).filter(Boolean));
    programmes = programmes
      .filter(P => P && keptProgs.has(P.id))
      .map(P => Object.assign({}, P, {
        /* a benefit may span programmes; prune its references to the projects that shipped,
           so nothing arrives pointing at a project the recipient was never given */
        benefits: (Array.isArray(P.benefits) ? P.benefits : []).map(b => Object.assign({}, b, {
          projectIds: (Array.isArray(b.projectIds) ? b.projectIds : []).filter(x => keptProjects.has(x))
        }))
      }));
  }
  const out = {
    schemaVersion: db.schemaVersion,
    savedAt: db.savedAt,
    programmes: programmes,
    projects: projects
  };
  /* ── v14 (W4a): the change counters and tombstones go into the BYTES here ───────────────
     W2 built the machinery and deliberately left the wiring to this wave, because the writer
     id is launcher-supplied and stamping a provisional one into real files would have meant
     rewriting them immediately.

     ⚠ WHY A STAGED DEEP COPY. `out.projects` above holds the LIVE project objects (only the
     active one is a shallow clone), so stamping revs onto them here would mutate window.__DB__
     — and step 3's whole contract, documented since v10.4 W1, is that it builds the bytes
     "without mutating anything". The plan/apply split exists for exactly this: `_revPlan`
     computes, this stages, and `_revApply` writes to the live records in step 4 on SUCCESS
     only, so a failed write can never leave a rev bumped for bytes that never reached disk.
     The copy is byte-neutral: everything serializeDB emits goes through JSON.stringify anyway,
     and a JSON round trip drops only undefined-valued keys, which serializeDB already treats
     as absent.

     ⚠ EXPORT PASSES A PLAN TOO, and must. `export_equals_save` compares a select-all export
     against a save byte-for-byte, and a backup that differs from what your next save would
     write is not a backup of your saved state. Export simply never calls `_revApply`, so it
     publishes nothing — which is the honest difference between taking a copy and saving. */
  if (!plan) return out;
  const staged = JSON.parse(JSON.stringify(out));
  const _w = _composeWriterId(_writerUser, _writerMachine);
  if (_w) staged.writer = _w;
  _revApply(staged, plan);
  /* Tombstones carried since load, plus the ones this save's plan just produced. A subset
     export silently drops any CHILD tombstone whose parent it did not include —
     _revResolveColl returns null. */
  _revFoldTombstones(staged, _revTombs.concat(_revTombRecords(plan)));
  /* ⚠ v15.2: …but TOP-LEVEL project and programme tombstones always resolve at root, so a
     subset export was shipping every deletion in the whole portfolio — the deleted things'
     identifiers, who deleted them and when — breaching A11's "only the selected project(s)"
     boundary, and each one resurrected as a ghost husk at the recipient before the import
     harvest existed. A deleted project is by definition never in the selection, so on a
     genuine SUBSET every top-level tombstone goes; the SAVE path (ids === null) and the
     select-all backup keep them all, because there they ARE the delete-propagation mechanism. */
  if (ids && projects.length < list.length) {
    staged.projects = staged.projects.filter(c => !(c && c.del));
    if (Array.isArray(staged.programmes)) staged.programmes = staged.programmes.filter(P => !(P && P.del));
  }
  return staged;
}

/* Step 4 - COMMIT. Save only. "This is now persisted" bookkeeping and NOTHING that
   affects the bytes: everything here is either non-serialised state or the in-memory
   application of a bake that step 3 already wrote into the file. */
function _commitSave(baked, captured) {
  /* v2.22: any pending delete-undo is committed on save (toast goes away). Export
     deliberately does NOT do this - taking a backup must not cost you your undo. */
  if (typeof clearPendingUndo === 'function') clearPendingUndo();
  /* v12.1 (W2, ◆E): remember WHERE he saved from, so the next launch resumes there.
     Save-only and after the write succeeded, which is what makes an unsaved session leave
     no trace and reopen on the portfolio. Per MACHINE (IndexedDB), never in the data file —
     the v11 reasoning: a UI preference must not diverge two colleagues' files. Changes no
     bytes, so it respects step 4's contract. */
  if (typeof _saveLastView === 'function') _saveLastView(location.hash || '');
  /* ── ⚠⚠ v16.8 (W1): WHAT HAPPENED DURING THE WRITE IS NOT DISCARDED ───────────────
     These three lines used to read:
         if (baked) ACTIONS.splice(0, ACTIONS.length, ...baked);
         newActions.length = 0;
         deletedIds.clear();
     and their comment said the two lists "are reset because they're now baked into ACTIONS".
     ⚠ THAT IS TRUE ONLY OF WHAT THE CAPTURE SAW. A write is asynchronous and nothing suspends
     the UI during it, so an action ADDED after the capture was thrown away and a deletion MADE
     after it was reversed — SWEEP-2 finding 2. And reached from the other side, an Undo pressed
     while the file was writing restored the record visibly and the completion then took it away
     again: you watch it come back and then disappear (finding 4, F-GAP-1).
     ⚠⚠ THE FILE WAS ALWAYS CORRECT FOR WHAT WAS CAPTURED — the loss happened HERE, in memory,
     after the write. That is why no bytes-only check could ever have caught it, and why
     v13.1 (A05)'s dirty flag below correctly reported unsaved work that no longer existed.
     ⚠ `captured` is what the pending lists held at the moment step 3 froze the bytes. Anything
     outside it happened DURING the write and is the user's newest intent, so it survives. */
  const _capDel = (captured && captured.deletes instanceof Set) ? captured.deletes : null;
  const _capNew = (captured && captured.newIds  instanceof Set) ? captured.newIds  : null;
  if (baked) {
    const _bakedIds = new Set(baked.map(a => a && a.id));
    /* Live now, absent from the bake, and NOT currently deleted → it arrived (or came back)
       after the capture. ⚠ The `deletedIds` test is what separates the two cases that look
       identical here: an ordinary delete leaves its record in ACTIONS and only adds the id to
       the set (see deleteAction), so without it every normal deletion would be resurrected. */
    const _survivors = ACTIONS.filter(a => a && !_bakedIds.has(a.id) && !deletedIds.has(a.id));
    ACTIONS.splice(0, ACTIONS.length, ...baked, ..._survivors);
    /* ⚠⚠ v10.3 (W1)'s SECOND DEFENCE, needed on a real path for the first time. The bake
       PRUNES the edits record of every action it dropped, so a record brought back by an Undo
       pressed during the write returns with NO overlay — and render() dereferences
       `edits[a.id].parentId` one tick later without a guard. Restoring the record without its
       overlay would trade a silent loss for a thrown save completion, which is worse.
       ⚠ Found by execution, not by review: the battery's console watch caught it while every
       one of the five new checks was already green. */
    _survivors.forEach(a => { if (!edits[a.id]) initActionEdits(a); });
  }
  if (_capNew) {
    const _keep = newActions.filter(a => a && !_capNew.has(a.id));
    newActions.length = 0; _keep.forEach(a => newActions.push(a));
    _keep.forEach(a => { if (!edits[a.id]) initActionEdits(a); });
  } else newActions.length = 0;
  if (_capDel) _capDel.forEach(id => deletedIds.delete(id));
  else deletedIds.clear();
  /* v2.5: clear session-edited highlights now that everything is persisted */
  sessionEditedActionIds.clear();
  refreshAll();
}


/* ── EVENT LISTENERS ── */
document.getElementById('statusFilters').addEventListener('click',e=>{const btn=e.target.closest('.filter-btn');if(!btn)return;activeStatus=btn.dataset.filter;activeStatFilter=(activeStatus==='all'&&activePriority==='all')?'all':activeStatus;document.querySelectorAll('#statusFilters .filter-btn').forEach(b=>b.classList.toggle('active',b===btn));refreshAll();});
document.getElementById('priorityFilters').addEventListener('click',e=>{const btn=e.target.closest('.filter-btn');if(!btn)return;activePriority=btn.dataset.priority;activeStatFilter=(activeStatus==='all'&&activePriority==='all')?'all':'';document.querySelectorAll('#priorityFilters .filter-btn').forEach(b=>b.classList.toggle('active',b===btn));refreshAll();});
/* ── v13.1 (A24): refreshAll(), not render() ────────────────────────────────────────────
   render() paints the LIST. The board is painted by renderActionBoard(), and the only
   caller that knows to do both is refreshAll() (`if (actionsView === 'board') …`). So in
   Board view every keystroke here filtered a list nobody was looking at and left the board
   untouched — the search box appeared completely dead. getBoardTopLevel() already consumes
   `searchQuery`; nothing was ever asking it to. Same for the clear button. */
document.getElementById('searchInput').addEventListener('input',e=>{searchQuery=e.target.value.trim();document.querySelector('.search-wrap').classList.toggle('has-text', !!e.target.value);refreshAll();});
function clearSearch() { const inp=document.getElementById('searchInput'); inp.value=''; searchQuery=''; document.querySelector('.search-wrap').classList.remove('has-text'); refreshAll(); inp.focus(); }
/* ⚠ v14.2 (d): render() alone repaints the LIST. On the board this changed nothing at all -
   v13.1 A04's shape again, the board sitting outside the common render path - so choosing a
   sort while looking at the board did visibly nothing.
   ⚠⚠ v15.3 (c) CORRECTION: this comment used to end "Both controls now repaint whichever view
   is actually on screen." THAT WAS NOT TRUE — the sort FIELD dropdown below was fixed and the
   DIRECTION button (toggleSortDir) was not, so it kept calling a bare render() and the board
   ignored it for three releases. He found it: "sort by priority works but not the button
   high/low priority first". The claim was broader than the change; the fix now matches the
   sentence. */
document.getElementById('sortSelect').addEventListener('change',e=>{sortBy=e.target.value;updateSortDirBtn();render();if(actionsView==='board')renderActionBoard();});
document.getElementById('groupSelect').addEventListener('change',e=>{groupBy=(e.target.value==='owner')?'owner':'status';if(actionsView==='board')renderActionBoard();});
/* v2.6: meeting search wiring */
document.getElementById('sessionSearchInput').addEventListener('input', e => {
  sessionSearchQuery = e.target.value.trim().toLowerCase();
  document.querySelector('.session-search-wrap').classList.toggle('has-text', !!e.target.value);
  syncSessionsFromDOM();  // capture any pending typing before renderSessions wipes the DOM
  renderSessions();
});
function clearSessionSearch() {
  const inp = document.getElementById('sessionSearchInput');
  inp.value = '';
  sessionSearchQuery = '';
  document.querySelector('.session-search-wrap').classList.remove('has-text');
  syncSessionsFromDOM();
  renderSessions();
  inp.focus();
}
document.addEventListener('keydown',e=>{if(e.key==='Escape'){closeModal();closeConfirm();}});
/* v2.9: prompt browser to confirm if user tries to leave with unsaved changes.
   Modern browsers ignore the message and show a generic 'Leave site?' prompt;
   we just need to set returnValue to a non-empty string. */
window.addEventListener('beforeunload', function(e) {
  if (isDirty || _progressDraftChanged()) { e.preventDefault(); e.returnValue = ''; return ''; }
});

/* v8.6.3: the uiState snapshot — extracted from exportFile (which calls it on every save)
   so it is testable in isolation. Captures the same view state as v2.11 always did, PLUS
   collapsedSections: the ids of folded <details> sections across ALL panes (sections stay
   in the DOM whichever tab is active, so off-tab folds are captured too). Collapsed-only
   list — absent/empty means everything open, the default; stale ids self-clean because the
   snapshot is rebuilt from the live DOM at every save. Read expanded card IDs from the DOM
   (source of truth) — toggleCard/toggleSession only mutate the DOM, not a runtime Set. */
function captureUIState() {
  appMeta.uiState = {
    activeTab,
    activeStatus, activePriority, activeStatFilter,
    searchQuery, sortBy, sortDesc, hideCompleted, groupBy,
    sessionSortDesc, sessionSearchQuery,
    expandedActionIds: [...document.querySelectorAll('#actionsList .action-card.expanded')]
      .map(el => Number(el.dataset.id)).filter(n => !isNaN(n)),
    expandedSessionIds: [...document.querySelectorAll('#sessionList .session-card.expanded')]
      .map(el => Number(el.dataset.id)).filter(n => !isNaN(n)),
    collapsedSections: [...document.querySelectorAll('details.dash-section:not([open]), details.raid-section:not([open])')]
      .map(d => d.id).filter(Boolean),
  };
}

/* v8.6.3: fold persistence — apply uiState.collapsedSections to the <details> sections
   (17 as of v9.0: −dashSecSchedule −dashSecBenefits +kpiSection, +scheduleSection (v9.0); saved ids for removed
   sections skip silently and self-clean at the next save).
   ALWAYS resets every section first, so folds can never leak from the previously
   active project; then closes the saved ids. Unknown/renamed ids skip silently (graceful
   for files touched by other versions).
   v11.0.1: the reset TARGET is now conditional. A project WITH a saved fold list resets
   to open and then folds that list -- exactly as before. A project with NO saved list
   resets to CLOSED, so one whose folds have never been saved (a brand-new project, or
   one saved while a sibling was the active project) opens fully collapsed: the sections
   available are legible at a glance instead of a scroll of empty bodies. Absent and
   empty deliberately STOP meaning the same thing -- `collapsedSections: []` is a real
   answer ("everything open") and is honoured, which is what makes this a ONE-TIME
   default rather than a recurring one. */
function applyCollapsedSectionsFromState() {
  const hasList = !!(appMeta.uiState && Array.isArray(appMeta.uiState.collapsedSections));
  document.querySelectorAll('details.dash-section, details.raid-section').forEach(d => { d.open = hasList; });
  const ids = hasList ? appMeta.uiState.collapsedSections : [];
  ids.forEach(id => {
    const el = document.getElementById(String(id));
    if (el && el.matches('details.dash-section, details.raid-section')) el.open = false;
  });
}

/* v2.11: restore UI state from appMeta.uiState (saved at last exportFile).
   Graceful no-op if uiState absent (older files / first save). */
function restoreUIState() {
  /* v8.6.3: fold state first, and BEFORE the uiState guard — the applier resets all
     sections to open even when this project has no uiState, so a project switch never
     inherits the previous project's folds. */
  applyCollapsedSectionsFromState();
  if (!appMeta.uiState) return;
  const ui = appMeta.uiState;

  // Scalar state vars
  if (typeof ui.activeStatus       === 'string')  activeStatus       = ui.activeStatus;
  if (typeof ui.activePriority     === 'string')  activePriority     = ui.activePriority;
  if (typeof ui.activeStatFilter   === 'string')  activeStatFilter   = ui.activeStatFilter;
  if (typeof ui.searchQuery        === 'string')  searchQuery        = ui.searchQuery;
  if (typeof ui.sortBy             === 'string')  sortBy             = ui.sortBy;
  if (ui.groupBy === 'status' || ui.groupBy === 'owner') groupBy = ui.groupBy;   /* v14.2 (d) */
  if (typeof ui.sortDesc           === 'boolean') sortDesc           = ui.sortDesc;
  if (typeof ui.hideCompleted      === 'boolean') hideCompleted      = ui.hideCompleted;
  if (typeof ui.sessionSortDesc    === 'boolean') sessionSortDesc    = ui.sessionSortDesc;
  if (typeof ui.sessionSearchQuery === 'string')  sessionSearchQuery = ui.sessionSearchQuery;
  /* v2.15: expanded action card restoration is handled post-render by
     applyExpandedActionsFromState() — the buggy block here referenced a
     non-existent global expandedIds and threw a ReferenceError that
     silently aborted the rest of init. */

  // Sync DOM elements that mirror state (action filters, search inputs, button labels)
  const sInp = document.getElementById('searchInput');
  if (sInp) {
    sInp.value = searchQuery;
    const wrap = sInp.closest('.search-wrap');
    if (wrap) wrap.classList.toggle('has-text', !!searchQuery);
  }
  const ssInp = document.getElementById('sessionSearchInput');
  if (ssInp) {
    ssInp.value = sessionSearchQuery;
    const wrap = ssInp.closest('.search-wrap');
    if (wrap) wrap.classList.toggle('has-text', !!sessionSearchQuery);
  }
  const sortSel = document.getElementById('sortSelect');
  if (sortSel) sortSel.value = sortBy;
  const grpSel = document.getElementById('groupSelect');     /* v14.2 (d) */
  if (grpSel) grpSel.value = groupBy;

  // Filter buttons (status + priority): apply active class
  document.querySelectorAll('#statusFilters .filter-btn').forEach(b =>
    b.classList.toggle('active', b.dataset.filter === activeStatus));
  document.querySelectorAll('#priorityFilters .filter-btn').forEach(b =>
    b.classList.toggle('active', b.dataset.priority === activePriority));

  // Hide-completed button
  const hcBtn = document.getElementById('hideCompletedBtn');
  if (hcBtn) {
    /* v2.20: align with toggleHideCompleted() — was 'Show completed' / 'Hide completed' */
    hcBtn.textContent = hideCompleted ? 'Show closed' : 'Hide closed';
    hcBtn.classList.toggle('active', hideCompleted);
  }

  // Sort direction button (label is field-aware)
  if (typeof updateSortDirBtn === 'function') updateSortDirBtn();

  // Session sort button
  const sLbl = document.getElementById('sessionSortLabel');
  const sIco = document.getElementById('sessionSortIcon');
  if (sLbl) sLbl.textContent = sessionSortDesc ? 'Newest first' : 'Oldest first';
  if (sIco) sIco.innerHTML = sessionSortDesc
    ? '<path d="M6 1v9M3 7l3 3 3-3"/>'
    : '<path d="M6 11V2M3 5l3-3 3 3"/>';

  // Active tab — set classes directly (avoid switchTab to keep render order clean)
  /* ⚠ v14.4 (d): the v8.5 LEGACY TAB MAPPING was removed here — 'raci' → 'stakeholders' and
     'notes' → 'changes', for tab ids last written by a v8.4-era file. Census: `activeTab` is
     only ever assigned the six live ids or this saved value, and schema 10 REFUSES every
     pre-v14 file outright, so no file this app can read is able to carry either string. It is
     pre-14 back-compat by definition, which is exactly what his 2026-08-03 clean break spent
     ("there are no pre-14 schemas, end of story"). An unknown id now simply falls through and
     matches no panel, leaving the boot default — the same outcome the mapping produced for
     every other stale id it did not name. */
  if (typeof ui.activeTab === 'string') {
    activeTab = ui.activeTab;
    const allTabs = ['dashboard', 'scope', 'actions', 'stakeholders', 'risks', 'changes'];
    allTabs.forEach(t => {
      const panel = document.getElementById('panel-' + t);
      const btn = document.getElementById('tab-' + t);
      if (panel) panel.classList.toggle('active', activeTab === t);
      _setSelected(btn, activeTab === t);   /* v13.1 (A58) */
    });
    const ac = document.getElementById('actionsControls');
    if (ac) ac.style.display = activeTab === 'actions' ? 'flex' : 'none';
  }
}
function applyExpandedSessionsFromState() {
  if (!appMeta.uiState || !Array.isArray(appMeta.uiState.expandedSessionIds)) return;
  const saved = new Set(appMeta.uiState.expandedSessionIds);
  document.querySelectorAll('#sessionList .session-card').forEach(el => {
    const id = Number(el.dataset.id);
    if (!isNaN(id)) el.classList.toggle('expanded', saved.has(id));
  });
}
/* v2.15: same pattern as applyExpandedSessionsFromState but for action cards.
   Replaces the v2.11 attempt to mutate a non-existent global expandedIds Set.
   Called after render() so the cards exist in DOM. */
function applyExpandedActionsFromState() {
  if (!appMeta.uiState || !Array.isArray(appMeta.uiState.expandedActionIds)) return;
  const saved = new Set(appMeta.uiState.expandedActionIds);
  document.querySelectorAll('#actionsList .action-card').forEach(el => {
    const id = Number(el.dataset.id);
    if (!isNaN(id)) el.classList.toggle('expanded', saved.has(id));
  });
}

// Preserve the legacy project stamp for older apps; SD-01 presentation reads __DB__.savedAt.
if (!appMeta.lastSaved) appMeta.lastSaved = _todayISO();
renderHeaderMeta();
/* v2.40 / v3.0.2: baseline lifecycle.
   The baseline (sessionSnapshot) is anchored to an explicit point in time.
   It moves ONLY on the user clicking "Reset tracker" — never on calendar-day
   boundary, never on addSession, never on duplicateLatestSession.

   (Historical note: v2.40 comments originally claimed the baseline moved on
    addSession and duplicateLatestSession too, but the corresponding
    takeSnapshot() calls were never implemented. v3.0.2 made the actual
    explicit-only behavior official after the user confirmed it as desired,
    and cleaned up the orphaned `sessionSnapshotIsStale` flag that was
    declared "GONE" in v2.40 comments but kept as a dead always-false
    variable.)

   At boot, ALWAYS restore the persisted sessionSnapshot if present;
   otherwise take a fresh one from current state (first-time file or
   never-saved state). */
let sessionSnapshot;
{
  const persisted = appMeta.sessionSnapshot;
  if (persisted && typeof persisted === 'object') {
    /* Restore the persisted baseline regardless of calendar day. */
    sessionSnapshot = persisted;
  } else {
    /* No persisted baseline (e.g. first-ever boot of a fresh file). Take a fresh
       snapshot — the next addSession will treat current state as the baseline,
       which is correct for a brand-new file. */
    sessionSnapshot = takeSnapshot();
  }
}
/* v2.11: restore UI state from saved file BEFORE first render so render uses the right vars */
restoreUIState();
updateCounts(); renderStats(); render();
/* v3.0: render stakeholders + mendelow grid at boot so the panel is fully rendered
   regardless of which tab the user lands on. */
renderStakeholders();
renderMendelowGrid();
/* v3.1: wire up the new stakeholder search + sort toolbar controls. */
_stkBootControls();
/* v8.2.2 (Q5): keep contenteditable placeholders reliable. Some browsers leave a stray
   <br> in a cleared contenteditable, so :empty (and therefore the data-ph placeholder) stops
   matching and the hint never returns. On focus-out, if a contenteditable field holds no
   meaningful text, clear its markup so it is truly :empty and the placeholder reappears.
   One delegated listener covers every :empty::before field — scope bullets, benefit cells,
   change-request description/requester, schedule/budget, stakeholder and risk fields. */
document.addEventListener('focusout', (e) => {
  const el = e.target;
  if (el && el.nodeType === 1 && el.isContentEditable &&
      (!el.textContent || !el.textContent.trim()) && el.innerHTML !== '') {
    el.innerHTML = '';
  }
});
/* ── v10.3 (W4): PLAIN-TEXT PASTE for every contenteditable ──────────────────────────
   There was no paste handling anywhere in the app, and every contenteditable commits
   `this.textContent` on blur. Three everyday failures followed from that:
     • pasting two lines from Outlook/Word yields <p>a</p><p>b</p>, and textContent
       concatenates block elements with NO separator — "Migrate tenants" + "Complete UAT"
       was stored as "Migrate tenantsComplete UAT", irreversibly;
     • the pasted markup (fonts, colours, tables) stayed live in the card for the rest of
       the session, wrecking the typography, while the STORED value was the mangled text;
     • pasting an image gave textContent '' — the blur handler stored empty and the Q5
       normaliser above then wiped the field, so the paste vanished silently.
   Take text/plain only, collapse newlines to single spaces (these are one-line fields;
   the charter <textarea> is a real textarea and keeps native multi-line paste), and insert
   via the selection so the browser's own undo stack still works. */
document.addEventListener('paste', (e) => {
  const el = e.target;
  if (!el || el.nodeType !== 1 || !el.isContentEditable) return;   /* inputs/textareas untouched */
  const cd = e.clipboardData || window.clipboardData;
  if (!cd) return;
  e.preventDefault();
  const text = (cd.getData('text/plain') || '').replace(/\s*[\r\n]+\s*/g, ' ').trim();
  if (!text) return;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) { el.textContent = (el.textContent || '') + text; return; }
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node); range.collapse(true);
  sel.removeAllRanges(); sel.addRange(range);
});
/* v3.2: dashboard init is deferred to the end of the script (after the v3.2
   module + its const declarations are defined) to avoid a const TDZ error —
   see the _initDashboardV32() call near the end of this file. */

